import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories } from '../redux/thunks/categoryThunks';
import { RootState, AppDispatch } from '../redux/store';
import Sidebar from './Sidebar';
import NavbarApp from './navbarApp';
import { createAlbums, fetchAlbums } from '../redux/thunks/albumThunks';
import { useNavigate } from 'react-router-dom';

const CreateAlbum: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { categories, status, errorMessage } = useSelector((state: RootState) => state.categories);
    const user = useSelector((state: RootState) => state.auth.user);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [thumbnailType, setThumbnailType] = useState<'file' | 'text' | 'default'>('default');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    type FormDataType = {
        title: string;
        thumbnail: File | string | null;
        description: string;
        usedToken: number;
        accessType: string;
        likes: number;
        tags: string;
        sharedWith: string;
        downloadable: boolean;
        status: string;
    };

    const [formData, setFormData] = useState<FormDataType>({
        title: '',
        thumbnail: null,
        description: '',
        usedToken: 5,
        accessType: 'private',
        likes: 0,
        tags: '',
        sharedWith: '',
        downloadable: false,
        status: 'active',
    });

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!formData.thumbnail) newErrors.thumbnail = 'Thumbnail is required';

        if (formData.sharedWith) {
            const emails = formData.sharedWith.split(',').map(email => email.trim());
            const invalidEmails = emails.filter(email => !/^\S+@\S+\.\S+$/.test(email));
            if (invalidEmails.length > 0) newErrors.sharedWith = 'One or more emails are invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked,
            }));
        } else if (type === 'file') {
            const file = (e.target as HTMLInputElement).files?.[0] || null;
            setFormData(prev => ({
                ...prev,
                thumbnail: file,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'number' ? Number(value) : value,
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            title: formData.title,
            thumbnail: formData.thumbnail,
            description: formData.description,
            usedToken: Number(formData.usedToken),
            accessType: formData.accessType,
            likes: Number(formData.likes),
            tags: formData.tags.split(',').map(tag => tag.trim()),
            sharedWith: formData.sharedWith
                ? formData.sharedWith.split(',').map(email => email.trim())
                : [],
            downloadable: formData.downloadable === true,
            status: formData.status,
            customerId: user?.id || null,
        };
    
        try {
            const response = await dispatch(createAlbums(payload)).unwrap();
            if (response.success) {
                const pngBase64 = response?.album?.qrCode;
        
                if (pngBase64 && pngBase64.startsWith('data:image/png;base64,')) {
                    const link = document.createElement('a');
                    link.href = pngBase64;
                    link.download = 'album-QR.png';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    console.warn('Invalid PNG data');
                }
        
                dispatch(fetchAlbums({ customerId: payload?.customerId }));
                navigate('/dashboard');
            } else {
                alert(response.message);
            }
        } catch (error) {
            console.error('Error while creating album:', error);
        }
        
        
    };

    useEffect(() => {
        dispatch(fetchCategories());
    }, [dispatch]);

    if (status === 'loading') return <div>Loading...</div>;
    if (status === 'failed') return <div>Error: {errorMessage}</div>;

    return (
        <div className="min-h-screen bg-black text-white flex flex-col">
            <NavbarApp user={user} setSidebarOpen={setSidebarOpen}/>

            {/* Mobile Sidebar */}
            <div className={`fixed inset-0 z-40 md:hidden transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="relative w-64 bg-gray-900 h-full">
                    <Sidebar />
                </div>
            </div>

            <div className="flex flex-1">
                {/* Desktop Sidebar */}
                <div className="hidden md:block w-64 bg-gray-900">
                    <Sidebar />
                </div>

                {/* Main Content */}
                <div className="flex-1 p-6">
                    <div className="max-w-2xl mx-auto bg-white text-black p-6 rounded-xl shadow-md">
                        <h2 className="text-xl font-bold mb-4">Create New Album</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    name="title"
                                    placeholder="Album Title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded"
                                />
                                {errors.title && <p className="text-red-600 text-sm">{errors.title}</p>}
                            </div>

                            <div>
                                <label className="block font-semibold mb-2">Thumbnail Input Type:</label>
                                <div className="flex gap-4">
                                    {['file', 'text', 'default'].map(type => (
                                        <label key={type}>
                                            <input
                                                type="radio"
                                                name="thumbnailType"
                                                value={type}
                                                checked={thumbnailType === type}
                                                onChange={() => setThumbnailType(type as any)}
                                            />
                                            <span className="ml-2 capitalize">{type}</span>
                                        </label>
                                    ))}
                                </div>

                                {thumbnailType === 'file' && (
                                    <input
                                        type="file"
                                        name="thumbnail"
                                        onChange={(e) =>
                                            setFormData({ ...formData, thumbnail: e.target.files?.[0] || null })
                                        }
                                        className="mt-2 w-full border p-2 rounded"
                                    />
                                )}

                                {thumbnailType === 'text' && (
                                    <input
                                        type="text"
                                        name="thumbnail"
                                        placeholder="Thumbnail URL"
                                        value={typeof formData.thumbnail === 'string' ? formData.thumbnail : ''}
                                        onChange={(e) =>
                                            setFormData({ ...formData, thumbnail: e.target.value })
                                        }
                                        className="mt-2 w-full border p-2 rounded"
                                    />
                                )}

                                {thumbnailType === 'default' && (
                                    <select
                                        name="thumbnail"
                                        value={typeof formData.thumbnail === 'string' ? formData.thumbnail : ''}
                                        onChange={(e) =>
                                            setFormData({ ...formData, thumbnail: e.target.value })
                                        }
                                        className="mt-2 w-full border p-2 rounded"
                                    >
                                        <option value="">Select a default thumbnail</option>
                                        {categories.map((category) => (
                                            <option key={category.id} value={category.url}>{category.name}</option>
                                        ))}
                                    </select>
                                )}

                                {errors.thumbnail && (
                                    <p className="text-red-600 text-sm mt-1">{errors.thumbnail}</p>
                                )}
                            </div>

                            <div>
                                <textarea
                                    name="description"
                                    placeholder="Album Description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded"
                                />
                                {errors.description && <p className="text-red-600 text-sm">{errors.description}</p>}
                            </div>

                            <input
                                type="text"
                                name="tags"
                                placeholder="Tags (comma separated)"
                                value={formData.tags}
                                onChange={handleChange}
                                className="w-full border p-2 rounded"
                            />

                            <div>
                                <input
                                    type="text"
                                    name="sharedWith"
                                    placeholder="Shared With Emails (comma separated)"
                                    value={formData.sharedWith}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded"
                                />
                                {errors.sharedWith && <p className="text-red-600 text-sm">{errors.sharedWith}</p>}
                            </div>

                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="downloadable"
                                    checked={formData.downloadable}
                                    onChange={handleChange}
                                />
                                <span>Downloadable</span>
                            </label>

                            <label className="flex items-center space-x-2">
                                <span>Status</span>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </label>

                            <button
                                type="submit"
                                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition"
                            >
                                Create Album
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateAlbum;
